import React, { createRef, useEffect, useState, useCallback, useMemo } from 'react';
import { CustomInput, ToggleButton, UseFormDropDown } from '../../components/common';
import Modal from '../../components/common/modal';
import AlertBox from '../../components/route-leaving-prompt/alert-box';
import { Resource, useAddResourceV3, useUpdateResourceV3, useRemoveResource } from '../../hooks/useResources';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { decimalOnChange, numberonBlur, onBlur } from '../../components/common/useForm';
import { use2026TagGroups } from '../../hooks/useTagGroups';
import { useVerticals, useDomains } from '../../hooks/useClients';
import { ResourceTagsEditModal } from './resource-tags-modal';
import { Checkbox } from '@mui/material';
import RichTextEditor from '../../components/rich-text-editor/rich-text-editor';

const resourceInputClassName =
  'rounded-lg border border-[#e5e7eb] bg-[#f9fafb] text-gray-900 focus:border-blue-primary focus:outline-none focus:ring-1 focus:ring-blue-primary';

type props = {
  resource: Resource;
};

const EditResourceDetails: React.FunctionComponent<props> = ({ resource }): JSX.Element => {
  const { updateResource } = useUpdateResourceV3(resource.id ?? 0);
  const { addResource } = useAddResourceV3();
  const { removeResource } = useRemoveResource(resource.id!);
  const { data: verticals = [] } = useVerticals();
  const { data: domains = [] } = useDomains();
  const [modal, setModal] = useState<boolean>(false);
  const [alertModal, setAlertModal] = useState<boolean>(false);
  const [enableSaveOption, setEnableSaveOption] = useState(false);
  const [errorMessages, setErrorMessages] = useState<{ phone_number?: string }>({});
  const [imageBase64, setImageBase64] = useState<string>();
  const [resourceLogoWide, setResourceLogoWide] = useState<string>();
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [enableForAllClients, setEnableForAllClients] = useState(false);
  const [enableForVerticalIds, setEnableForVerticalIds] = useState<number[]>([]);
  const [enableForDomainId, setEnableForDomainId] = useState<number | null>(null);

  const { allTags: tags } = use2026TagGroups();
  const isGlobalResource = resource.client_id == null;

  const fileInput: React.RefObject<HTMLInputElement> = createRef();
  const fileInputWide: React.RefObject<HTMLInputElement> = createRef();

  const defaultResource = {
    ...resource,
    description: resource?.description || '',
    long_description: resource?.long_description || '',
    meta_keywords: resource?.meta_keywords || '',
    image_url: resource?.image_url || null,
    wide_image_url: resource?.wide_image_url || null,
    name: resource?.name || '',
    phone_number: resource?.phone_number || '',
    sort_weight: resource?.sort_weight || 0,
    status: resource?.status || 'live',
    url: resource?.url || '',
    tag_ids: resource?.tag_ids || [],
    is_pre_crisis: resource?.is_pre_crisis || false,
    // Not storing domain_id on resource for now; domain is only used for enable_for filtering
    domain_id: null as number | null,
  };

  const handleTagsChange = (tags: number[]) => {
    setValue('tag_ids', tags, { shouldDirty: true });
    setShowTagsModal(false);
  };

  const onFileChange = (event: React.FormEvent<HTMLInputElement>, wideImage?: boolean) => {
    try {
      if (event.currentTarget.files?.[0]) {
        let reader = new FileReader();

        reader.onload = (e: any) => {
          //Set the Base64 string return from FileReader as source.
          const image = new Image();

          if (wideImage) {
            setValue('wide_image_url', e.target.result);
            setResourceLogoWide(e.target.result);
            setEnableSaveOption(true);
          } else {
            //Validate the File Height and Width.
            image.onload = () => {
              // @ts-ignore
              const height = image.height;
              // @ts-ignore
              const width = image.width;
              if (height === width) {
                setValue('image_url', e.target.result);
                setImageBase64(e.target.result);
                setEnableSaveOption(true);
              } else {
                toast.error('Image must be equal height and width.');
                return new Error('Image must be equal height and width.');
              }
            };
          }
          image.src = e.target.result;
        };
        reader.readAsDataURL(event.currentTarget.files[0]);
      }
    } catch (error) {
      console.error('err--', error);
    }
  };

  const handleImageInput = (name: string) => {
    if (name === 'image_url') {
      fileInput.current?.click();
    }
    if (name === 'wide_image_url') {
      fileInputWide.current?.click();
    }
  };

  const setImageDisplay = useCallback(() => {
    if (resource?.image_url) {
      setImageBase64(resource.image_url);
    } else if (imageBase64 === '') {
      setImageBase64('');
    }

    if (resource?.wide_image_url) {
      setResourceLogoWide(resource.wide_image_url);
    } else if (resourceLogoWide === '') {
      setResourceLogoWide('');
    }
  }, [resource, imageBase64, resourceLogoWide]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: useMemo(() => {
      setImageDisplay();
      if (resource) {
        return defaultResource;
      }
    }, [resource]),
  });

  const validatePhone = async (data: { phone_number?: string }) => {
    let isValid = false;
    const raw = data?.phone_number ?? '';
    const hasAlphabeticCharacters = /[a-zA-Z]/.test(raw);
    // Remove non digits and validate that the number is either 0 digits (null), exactly 3 digits (N11 codes),
    // or between 10 and 15 digits (phone numbers ranging from US area code + number to maximum international length)
    const phoneNumber = raw.replace(/\D/g, '');
    const phoneNumberRegex = /^\d{3}$|^\d{10,15}$/;
    if (hasAlphabeticCharacters) {
      setErrorMessages({ phone_number: 'Phone number cannot contain alphabetic characters' });
    } else if (phoneNumber.length > 0 && !phoneNumberRegex.test(phoneNumber)) {
      setErrorMessages({ phone_number: 'Please enter a valid 3 or 10-15 digit phone number' });
    } else {
      setErrorMessages({});
      isValid = true;
    }

    return isValid;
  };

  useEffect(() => {
    if (resource.id) {
      reset(defaultResource);
      setImageBase64(resource?.image_url || '');
      setResourceLogoWide(resource?.wide_image_url || '');
      setEnableSaveOption(false);
      setEnableForDomainId(null); // domain only used for enable_for filtering, not stored on resource
    }
  }, [resource]);

  const getEnableForPayload = ():
    | null
    | 'all'
    | { vertical_ids: number[] }
    | { domain_id: number; vertical_ids: number[] } => {
    if (enableForAllClients) return 'all';
    if (enableForVerticalIds.length > 0) {
      if (enableForDomainId != null) {
        return { domain_id: enableForDomainId, vertical_ids: enableForVerticalIds };
      }
      return { vertical_ids: enableForVerticalIds };
    }
    // Domain only: enable for all clients in that domain
    if (enableForDomainId != null) {
      return { domain_id: enableForDomainId, vertical_ids: [] };
    }
    return null;
  };

  const handleSave = async (data: any) => {
    const { domain_id: _domainId, ...rest } = data;
    const payload: Record<string, unknown> = {
      ...rest,
      phone_number: data?.phone_number ? data?.phone_number.replace(/[^\d+]/g, '') : null,
      image_url: data?.image_url ? (enableSaveOption ? data?.image_url : undefined) : null,
      wide_image_url: data?.wide_image_url ? (enableSaveOption ? data?.wide_image_url : undefined) : null,
    };
    const enable_for = isGlobalResource ? getEnableForPayload() : null;
    // Not storing domain_id on resource for now; domain is only used for enable_for filtering
    if (enable_for != null) {
      payload.enable_for = enable_for;
    }

    const clearEnableForFields = () => {
      setEnableForDomainId(null);
      setEnableForVerticalIds([]);
      setEnableForAllClients(false);
      setValue('domain_id', null);
    };

    if ((await validatePhone(data)) && resource?.id !== undefined) {
      updateResource.mutate(payload, {
        onSuccess: () => {
          toast.success('Resource updated successfully');
        },
      });
      setModal(false);
      setAlertModal(false);
      setEnableSaveOption(false);
      clearEnableForFields();
      imageBase64 && setImageBase64('');
      resourceLogoWide && setResourceLogoWide('');
    } else if ((await validatePhone(data)) && resource?.id === undefined) {
      addResource.mutate(payload, {
        onSuccess: () => {
          toast.success('Resource created successfully');
        },
      });
      setModal(false);
      setAlertModal(false);
      setEnableSaveOption(false);
      clearEnableForFields();
      reset();
      imageBase64 && setImageBase64('');
      resourceLogoWide && setResourceLogoWide('');
    }
  };

  const handleRemoveResource = async () => {
    removeResource.mutate();
    setModal(false);
    setAlertModal(false);
    setEnableSaveOption(false);
    reset();
  };

  return (
    <>
      <button
        onClick={(event) => {
          event.stopPropagation();
          setModal(true);
        }}
        className="bg-blue-primary rounded-lg px-4 py-2.5 text-center text-sm font-medium text-white transition-colors hover:opacity-90"
      >
        {resource.id ? 'Edit' : 'Add Resource +'}
      </button>
      <Modal
        isModel={modal}
        InnerComponent={
          <div
            onClick={(event) => {
              event.stopPropagation();
            }}
            className="flex max-h-[90vh] w-full max-w-6xl transform flex-col overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-lg transition-all sm:my-8 sm:align-middle"
          >
            <form onSubmit={handleSubmit(handleSave)} id="resource-form" className="flex min-h-0 flex-1 flex-col">
              <div className="flex min-h-0 flex-1 flex-col bg-white px-5 pt-5 sm:px-6 sm:pt-6">
                <div className="flex-shrink-0 border-b border-[#e5e7eb] pb-4">
                  <h1 className="text-2xl font-semibold leading-tight text-black">
                    {resource.id ? 'Edit Resource' : 'Add Resource'}
                  </h1>
                </div>
                <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="min-w-0 space-y-3 overflow-y-auto rounded-lg px-4 py-5 lg:min-w-[320px]">
                    <div className="grid grid-cols-2 gap-4 gap-y-5">
                      <div className="col-span-2">
                        <CustomInput
                          inputType="text"
                          Label="Resource Name"
                          register={{
                            ...register('name', {
                              required: 'Please provide resource name',
                              onBlur: (e: any) => onBlur(e, 'name', setValue),
                            }),
                          }}
                          //@ts-ignore
                          Error={!!errors?.name?.message?.length}
                          ErrorMessage={errors.name?.message}
                          dataTestId="resource-name"
                          inputClassName={resourceInputClassName}
                        />
                      </div>
                      <div className="col-span-2 text-left">
                        <UseFormDropDown label={'Status'}>
                          <select
                            className="focus:border-blue-primary focus:ring-blue-primary w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1"
                            defaultValue="published"
                            {...register('status')}
                            data-testid="resource-edit-status"
                          >
                            <option value="live">Live</option>
                            <option value="hidden">Hidden</option>
                          </select>
                        </UseFormDropDown>
                      </div>
                      <div>
                        <CustomInput
                          inputType="tel"
                          Label="Phone"
                          register={{
                            ...register('phone_number', {
                              onBlur: (e: any) => onBlur(e, 'phone_number', setValue),
                              onChange: (e) => {
                                validatePhone({ phone_number: e.target.value });
                              },
                            }),
                          }}
                          //@ts-ignore
                          Error={errorMessages.phone_number?.length > 0 ? true : false}
                          ErrorMessage={errorMessages.phone_number}
                          dataTestId="resource-phone-number"
                          inputClassName={resourceInputClassName}
                        />
                      </div>
                      <div>
                        <CustomInput
                          inputType="text"
                          Label="Link"
                          register={{
                            ...register('url', {
                              required: 'Please provide resource link',
                              onBlur: (e: any) => onBlur(e, 'url', setValue),
                            }),
                          }}
                          //@ts-ignore
                          Error={!!errors?.url?.message?.length}
                          ErrorMessage={errors.url?.message}
                          dataTestId="resource-link"
                          inputClassName={resourceInputClassName}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="mb-2 block text-left text-sm font-medium leading-4 text-black">
                          Description
                        </label>
                        <div className="my-2 w-full">
                          <input
                            type="hidden"
                            {...register('description', {
                              required: 'Please provide resource description',
                            })}
                          />
                          <RichTextEditor
                            content={watch('description') || ''}
                            onChange={(value) =>
                              setValue('description', value, { shouldDirty: true, shouldValidate: true })
                            }
                            key={`resource-description-${resource.id ?? 'new'}`}
                          />
                          {Boolean(errors?.description?.message) && (
                            <label className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                              {errors.description?.message}
                            </label>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="mb-2 block text-left text-sm font-medium leading-4 text-black">
                          Long Description
                        </label>
                        <div className="my-2 w-full">
                          <input type="hidden" {...register('long_description')} />
                          <RichTextEditor
                            content={watch('long_description') || ''}
                            onChange={(value) => setValue('long_description', value, { shouldDirty: true })}
                            key={`resource-long-description-${resource.id ?? 'new'}`}
                          />
                          {Boolean(errors?.long_description?.message) && (
                            <label className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                              {errors.long_description?.message}
                            </label>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="mb-2 block text-left text-sm font-medium leading-4 text-black">
                          Meta Keywords
                        </label>
                        <div className="my-2 w-full">
                          <textarea
                            data-testid="resource-meta-keywords"
                            className="focus:border-blue-primary focus:ring-blue-primary h-28 w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1"
                            {...register('meta_keywords', {
                              onBlur: (e: any) => onBlur(e, 'meta_keywords', setValue),
                            })}
                          />
                          {Boolean(errors?.meta_keywords?.message) && (
                            <label className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                              {errors.meta_keywords?.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0 overflow-y-auto rounded-lg px-4 py-5 lg:min-w-[280px]">
                    <div className="flex flex-col gap-4">
                      <div className="text-left">
                        <p className="text-base font-semibold leading-5 text-black">Resource Image</p>
                        <p className="py-1 text-sm font-medium leading-6 text-[#62748e]">
                          Displays in app as benefit provider
                        </p>
                        <div className="w-full">
                          <div
                            className={`aspect-ratio-container flex w-full items-center justify-center rounded-lg border border-[#e5e7eb] bg-[#f9fafb] ${!imageBase64 && 'cursor-pointer'}`}
                            {...(!imageBase64 && { onClick: handleImageInput.bind(this, 'image_url') })}
                            data-testid="resource-edit-image-upload"
                          >
                            {imageBase64 ? (
                              <img className="border" src={imageBase64} alt="clientImage" />
                            ) : (
                              <div className="flex h-80 items-center justify-center">
                                <button
                                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl"
                                  onClick={(ev) => ev.preventDefault()}
                                >
                                  +
                                </button>
                                <input
                                  ref={fileInput}
                                  id="file-upload"
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onClick={() => {
                                    if (fileInput.current) fileInput.current.value = '';
                                  }}
                                  onChange={(data) => onFileChange(data)}
                                />
                              </div>
                            )}
                          </div>
                          {imageBase64 && (
                            <button
                              className="my-2 w-full rounded-lg border border-[#e5e7eb] bg-white px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
                              onClick={() => {
                                setImageBase64('');
                                setValue('image_url', '');
                                setEnableSaveOption(true);
                              }}
                              data-testid="resource-edit-remove-resource-image"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="text-base font-semibold leading-5 text-black">Wide Resource Image</p>
                        <p className="py-1 text-sm font-medium leading-6 text-[#62748e]">
                          Displays when peers are associated to resources
                        </p>
                        <p className="py-1 text-sm font-medium leading-6 text-red-600">
                          Wide resource images will look best with a 16:9 aspect ratio, a minimum resolution of 1280 x
                          720, and as a transparent .png.
                        </p>
                        <div className="flex w-full flex-col">
                          <div
                            className={`aspect-ratio-container w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] ${!resourceLogoWide && 'cursor-pointer'}`}
                            {...(!resourceLogoWide && { onClick: handleImageInput.bind(this, 'wide_image_url') })}
                            data-testid="resource-wide-image-upload"
                          >
                            {resourceLogoWide ? (
                              <div className="aspect-ratio-div">
                                <img
                                  className="aspect-ratio-container-img"
                                  src={resourceLogoWide}
                                  alt="resourceLogoWide"
                                />
                              </div>
                            ) : (
                              <div className="flex h-56 items-center justify-center">
                                <button
                                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl"
                                  onClick={(ev) => ev.preventDefault()}
                                >
                                  +
                                </button>
                                <input
                                  ref={fileInputWide}
                                  id="file-upload-wide"
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onClick={() => {
                                    if (fileInputWide.current) fileInputWide.current.value = '';
                                  }}
                                  onChange={(data) => onFileChange(data, true)}
                                />
                              </div>
                            )}
                          </div>
                          <p className="pt-2 text-sm text-gray-500">1280 x 720 px</p>
                          {resourceLogoWide && (
                            <button
                              className="my-2 w-full rounded-lg border border-[#e5e7eb] bg-white px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
                              onClick={() => {
                                setResourceLogoWide('');
                                setValue('wide_image_url', '');
                                setEnableSaveOption(true);
                              }}
                              data-testid="remove-resource-wide-image"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0 overflow-y-auto rounded-lg px-4 py-5 lg:min-w-[200px]">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-wrap gap-4">
                        <div className="min-w-0 flex-1">
                          <CustomInput
                            inputType="text"
                            Label="Sort Order"
                            register={{
                              ...register('sort_weight', {
                                onChange: (e) => decimalOnChange(e, 'sort_weight', setValue, 2),
                                onBlur: (e) => numberonBlur(e, 'sort_weight', setValue),
                                valueAsNumber: true,
                              }),
                            }}
                            //@ts-ignore
                            Error={!!errors?.sort_weight?.message?.length}
                            ErrorMessage={errors.sort_weight?.message}
                            dataTestId="resource-sort-order"
                            inputClassName={resourceInputClassName}
                          />
                        </div>
                        <div className="mt-4 flex shrink-0 items-center gap-2 py-2">
                          <span className="whitespace-nowrap text-sm font-medium text-black">Pre-Crisis</span>
                          <ToggleButton
                            id={'is_pre_crisis'}
                            status={watch('is_pre_crisis')}
                            initialstatus={false}
                            setStatus={(data: boolean) => {
                              setValue('is_pre_crisis', data);
                              setEnableSaveOption(true);
                            }}
                          />
                        </div>
                      </div>
                      <button
                        className="w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-4 py-2.5 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowTagsModal(true);
                        }}
                        data-testid="resource-tags"
                      >
                        TAGS
                      </button>
                      <div className="grid grid-flow-row grid-cols-2 gap-x-4 gap-y-1">
                        {getValues('tag_ids').map((id) => (
                          <div
                            key={id}
                            className="p-0.25 font-manrope block w-12 whitespace-nowrap text-sm font-medium leading-5 text-gray-800"
                          >
                            <div className="bg-blue-primary mr-2 inline-block h-2 w-2 rounded"></div>
                            {tags?.find((x) => x.id === id)?.name}
                          </div>
                        ))}
                      </div>
                      {isGlobalResource && (
                        <>
                          <div className="h-10 shrink-0" aria-hidden="true" />
                          <div className="flex flex-col gap-2 border-t border-[#e5e7eb] pt-6">
                            <h2 className="text-xl font-semibold leading-6 text-black">Add to client availability</h2>
                            <p className="mt-1 text-sm font-medium leading-6 text-[#62748e]">
                              Optionally add this resource to clients’ enabled resources when saving.
                            </p>
                            <UseFormDropDown label="Domain">
                              <select
                                className="focus:border-blue-primary focus:ring-blue-primary w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1"
                                {...register('domain_id', { valueAsNumber: true })}
                                onChange={(e) => {
                                  const val = e.target.value ? parseInt(e.target.value, 10) : null;
                                  setValue('domain_id', val);
                                  setEnableForDomainId(val);
                                  setEnableSaveOption(true);
                                }}
                                data-testid="resource-domain"
                              >
                                <option value="">No domain (global)</option>
                                {domains.map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {d.name}
                                  </option>
                                ))}
                              </select>
                            </UseFormDropDown>
                            <div className="mt-4 flex flex-col gap-2">
                              <label className="flex cursor-pointer items-center gap-2">
                                <Checkbox
                                  size="small"
                                  checked={enableForAllClients}
                                  onChange={(e) => {
                                    setEnableSaveOption(true);
                                    if (e.target.checked) {
                                      setEnableForAllClients(true);
                                      setEnableForVerticalIds([]);
                                    } else {
                                      setEnableForAllClients(false);
                                    }
                                  }}
                                  data-testid="resource-enable-for-all-clients"
                                />
                                <span>All clients</span>
                              </label>
                              {verticals.map((v) => (
                                <label key={v.id} className="flex cursor-pointer items-center gap-2">
                                  <Checkbox
                                    size="small"
                                    checked={enableForVerticalIds.includes(v.id)}
                                    onChange={(e) => {
                                      setEnableSaveOption(true);
                                      if (e.target.checked) {
                                        setEnableForAllClients(false);
                                        setEnableForVerticalIds((prev) => [...prev, v.id]);
                                      } else {
                                        setEnableForVerticalIds((prev) => prev.filter((id) => id !== v.id));
                                      }
                                    }}
                                    data-testid={`resource-enable-for-vertical-${v.id}`}
                                  />
                                  <span>{v.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3 border-t border-[#e5e7eb] bg-white px-5 py-5 sm:px-6 sm:py-5">
                <button
                  className={
                    'min-h-[2.5rem] min-w-[5rem] rounded-lg px-4 py-2 text-center text-sm font-medium transition-colors ' +
                    (isDirty || enableSaveOption
                      ? 'bg-blue-primary text-white hover:opacity-90'
                      : 'cursor-not-allowed bg-gray-200 text-gray-500')
                  }
                  type="submit"
                  form="resource-form"
                  disabled={!isDirty && !enableSaveOption}
                  data-testid="resource-edit-save-button"
                >
                  Save
                </button>
                <button
                  className="min-h-[2.5rem] min-w-[5rem] rounded-lg border border-[#e5e7eb] bg-white px-4 py-2 text-center text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  type="button"
                  onClick={() => {
                    if (isDirty || enableSaveOption) {
                      setAlertModal(true);
                    } else {
                      setModal(false);
                    }
                  }}
                  data-testid="resource-edit-cancel-button"
                >
                  Cancel
                </button>
                {resource.id && (
                  <button
                    className="min-h-[2.5rem] rounded-lg border border-red-200 bg-white px-4 py-2 text-center text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                    type="button"
                    onClick={handleRemoveResource}
                    data-testid="remove-resource"
                  >
                    Remove Resource
                  </button>
                )}
              </div>
            </form>
            {tags && showTagsModal && (
              <ResourceTagsEditModal
                onChange={handleTagsChange}
                onExit={() => setShowTagsModal(false)}
                selectedTagIds={getValues('tag_ids')}
                tags={tags}
              />
            )}
          </div>
        }
      />
      <AlertBox
        visible={alertModal}
        handlePrompt={() => {
          reset(defaultResource);
          setImageBase64('');
          setImageDisplay();
          setModal(false);
          setAlertModal(false);
          setEnableSaveOption(false);
        }}
        closeModal={() => {
          setAlertModal(false);
        }}
        confirmButtonText={'Discard Changes'}
        titleText={'Alert'}
        contentText={'You have unsaved changes. If you leave this screen without saving, your changes will be lost.'}
        cancelButtonText={'Cancel'}
      />
    </>
  );
};

export default EditResourceDetails;
